import { initializeConfiguration } from '@/utils/configManager';
import * as Sentry from '@sentry/react-native';
import { getCurrentUser } from 'aws-amplify/auth';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  tokenRedemptionInProgress: boolean;
  setIsAuthenticated: (value: boolean) => void;
  setError: (error: string | null) => void;
  setTokenRedemptionInProgress: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenRedemptionInProgress, setTokenRedemptionInProgress] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      setIsLoading(true);
      try {
        // Initialize configuration (load stored username and reconfigure if needed)
        await initializeConfiguration();
        
        const user = await getCurrentUser();
        if (user) {
          setIsAuthenticated(true);
          setError(null);
          
          // Set user context in Sentry for error tracking
          if (!__DEV__) {
            Sentry.setUser({
              id: user.username, // Use username as user ID
              username: user.username,
            });
          }
        } else {
          setIsAuthenticated(false);
          setError('Not signed in.');
        }
      } catch (err) {
        setIsAuthenticated(false);
        // Only show error if it's not UserUnAuthenticatedException
        if (
          err &&
          typeof err === 'object' &&
          'name' in err &&
          (err as any).name === 'UserUnAuthenticatedException'
        ) {
          // Do not set error for this specific exception
        } else {
          setError('Not signed in.' + err);
        }
      } finally {
        setIsLoading(false);
      }
    }
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      isLoading, 
      isAuthenticated, 
      error, 
      tokenRedemptionInProgress,
      setIsAuthenticated, 
      setError,
      setTokenRedemptionInProgress 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 